/**
 * TEMPORARY DIAGNOSTIC SCAFFOLDING -- safe to delete wholesale.
 *
 * Renders JS errors on screen so they can be read on a real iPhone, where no
 * console is reachable. Gated entirely behind VUE_APP_DEBUG_ERRORS=true.
 *
 * To remove: delete src/debug/, drop the two lines in src/main.js, unwrap
 * handleAddToCart in src/pages/ProductDetail.vue (search for debugTrace), and
 * drop the debugError import + call in src/store/index.js saveState.
 *
 * Deliberately plain DOM: no Vue, no store, no imports. It has to keep working
 * even when the app itself is broken, which is the whole point.
 */

const ENABLED = process.env.VUE_APP_DEBUG_ERRORS === "true";

let panel = null;
let list = null;
let count = 0;
// Vue re-reports some errors through more than one channel; do not print twice.
const reported = typeof WeakSet === "function" ? new WeakSet() : null;

export function isDebugErrorsEnabled() {
  return ENABLED;
}

function buildPanel() {
  if (panel || typeof document === "undefined" || !document.body) {
    return panel;
  }

  panel = document.createElement("div");
  panel.setAttribute("data-debug-error-overlay", "true");
  panel.style.cssText = [
    "position:fixed",
    "top:0",
    "left:0",
    "right:0",
    // above Vuetify dialogs/overlays, which sit around z-index 200-300
    "z-index:2147483647",
    "max-height:45vh",
    "overflow-y:auto",
    "-webkit-overflow-scrolling:touch",
    "background:#b00020",
    "color:#ffffff",
    "font-family:ui-monospace,SFMono-Regular,Menlo,Consolas,monospace",
    "font-size:11px",
    "line-height:1.35",
    "padding:6px 8px",
    "padding-top:calc(6px + env(safe-area-inset-top,0px))",
    "white-space:pre-wrap",
    "word-break:break-word",
    "box-shadow:0 2px 8px rgba(0,0,0,0.4)",
  ].join(";");

  const header = document.createElement("div");
  header.style.cssText = "font-weight:bold;opacity:0.85;margin-bottom:4px";
  header.textContent = "debug errors - tap to dismiss";
  panel.appendChild(header);

  list = document.createElement("div");
  panel.appendChild(list);

  panel.addEventListener("click", () => {
    panel.style.display = "none";
    list.textContent = "";
    count = 0;
  });

  document.body.appendChild(panel);
  return panel;
}

function append(kind, text) {
  if (!ENABLED) {
    return;
  }
  // body may not exist yet if something throws very early
  if (!buildPanel()) {
    window.setTimeout(() => append(kind, text), 50);
    return;
  }
  count += 1;
  const row = document.createElement("div");
  row.style.cssText = "margin:0 0 6px 0;padding:0 0 6px 0;border-bottom:1px solid #ffffff40";
  row.textContent = `#${count} [${kind}] ${text}`;
  list.appendChild(row);
  panel.style.display = "block";
  panel.scrollTop = panel.scrollHeight;
}

function describe(value) {
  if (value === null) return "null";
  if (value === undefined) return "undefined";
  if (value instanceof Error) {
    const bits = [`${value.name}: ${value.message}`];
    if (value.stack) bits.push(String(value.stack));
    return bits.join("\n");
  }
  if (typeof value === "object") {
    try {
      return JSON.stringify(value);
    } catch (e) {
      return String(value);
    }
  }
  return String(value);
}

function alreadyReported(err) {
  if (!reported || !err || typeof err !== "object") return false;
  if (reported.has(err)) return true;
  reported.add(err);
  return false;
}

/** A plain trace line -- use to prove a given statement was reached. */
export function debugTrace(message) {
  append("trace", String(message));
}

/** Report an error object with an arbitrary label. */
export function debugError(label, err) {
  if (!ENABLED) return;
  if (alreadyReported(err)) return;
  append("error", `${label}\n${describe(err)}`);
}

export function installErrorOverlay(Vue) {
  if (!ENABLED || typeof window === "undefined") {
    return;
  }

  window.onerror = function onError(message, source, lineno, colno, error) {
    if (!alreadyReported(error)) {
      const where = `${source || "?"}:${lineno || "?"}:${colno || "?"}`;
      const stack = error && error.stack ? `\n${error.stack}` : "";
      append("window.onerror", `${String(message)}\n${where}${stack}`);
    }
    return false;
  };

  window.addEventListener("unhandledrejection", (event) => {
    const reason = event && event.reason;
    if (!alreadyReported(reason)) {
      append("unhandledrejection", describe(reason));
    }
  });

  // Vue swallows errors thrown inside component hooks and event handlers into
  // its own handler -- they never reach window.onerror. Without this the very
  // errors we are hunting would stay invisible.
  if (Vue) {
    /* eslint-disable-next-line no-param-reassign */
    Vue.config.errorHandler = function onVueError(err, vm, info) {
      if (!alreadyReported(err)) {
        append("vue.errorHandler", `${info || "unknown hook"}\n${describe(err)}`);
      }
      if (typeof console !== "undefined" && console.error) {
        console.error(err);
      }
    };
  }

  append("trace", "error overlay active");
}

export default installErrorOverlay;
